import React, { forwardRef, useMemo, useState } from 'react';
import { getImageUrl } from '../config/api';
import { formatPledgeAmount } from '../utils/formatPledgeAmount';
import './PledgePage.css';

function coupleNameLines(names) {
  const upper = String(names || '').trim().toUpperCase();
  if (!upper) return ['OUR WEDDING'];
  const amp = upper.indexOf('&');
  if (amp === -1) return [upper];
  const first = upper.slice(0, amp + 1).trim();
  const second = upper.slice(amp + 1).trim();
  return second ? [first, second] : [first];
}

const PledgeCard = forwardRef(({ pledge, settings }, ref) => {
  const coupleNames = pledge?.couple_names || settings?.couple_names || 'Our Wedding';
  const coupleLines = useMemo(() => coupleNameLines(coupleNames), [coupleNames]);
  const imageUrl =
    pledge?.card_image ||
    pledge?.couple_image ||
    settings?.pledge_card_image ||
    settings?.pledge_couple_image;
  const resolvedImageUrl = useMemo(() => getImageUrl(imageUrl), [imageUrl]);
  const [imageFailed, setImageFailed] = useState(false);
  const bank = pledge?.bank_details || settings?.pledge_bank_details || '';
  const contact = pledge?.contact_info || settings?.pledge_contact_info || '';
  const currency = pledge?.currency || settings?.pledge_currency || 'KES';
  const guestName = pledge?.guest_name || 'Your Name Here';
  const showPhoto = Boolean(resolvedImageUrl && !imageFailed);

  return (
    <div className="pledge-card-printable" ref={ref}>
      <div className="pledge-card-inner">
        <aside className="pledge-card-photo">
          <div
            className={`pledge-card-photo-frame${showPhoto ? '' : ' pledge-card-photo-frame--empty'}`}
            data-photo-url={showPhoto ? resolvedImageUrl : undefined}
          >
            {showPhoto ? (
              <img
                src={resolvedImageUrl}
                alt=""
                aria-hidden="true"
                className="pledge-card-photo-img"
                crossOrigin="anonymous"
                decoding="async"
                draggable={false}
                onError={() => setImageFailed(true)}
              />
            ) : (
              <div className="pledge-card-photo-placeholder" aria-hidden="true" />
            )}
          </div>
          {bank ? <p className="pledge-card-bank">{bank}</p> : null}
        </aside>

        <div className="pledge-card-details">
          <header className="pledge-card-title-block">
            <span className="pledge-card-label-line" aria-hidden="true" />
            <p className="pledge-card-label">Pledge Card</p>
            <h3 className="pledge-card-couple" aria-label={coupleNames}>
              {coupleLines.map((line) => (
                <span key={line} className="pledge-card-couple-line">{line}</span>
              ))}
            </h3>
          </header>

          <div className="pledge-card-body">
            <span className="pledge-card-body-accent" aria-hidden="true" />
            <div className="pledge-card-body-content">
              <p className="pledge-card-guest">{guestName}</p>
              <p className="pledge-card-amount">
                {formatPledgeAmount(pledge?.amount, currency)}
              </p>
              {pledge?.reminder_date_formatted ? (
                <p className="pledge-card-reminder">Reminder: {pledge.reminder_date_formatted}</p>
              ) : null}
            </div>
          </div>

          {contact ? (
            <footer className="pledge-card-contact">
              {String(contact)
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line) => (
                  <span key={line} className="pledge-card-contact-line">
                    {line}
                  </span>
                ))}
            </footer>
          ) : null}
        </div>
      </div>
    </div>
  );
});

PledgeCard.displayName = 'PledgeCard';

export default PledgeCard;
